(() => {
  if (customElements.get('membership-widget')) return;

  class MembershipWidget extends HTMLElement {
    constructor() {
      super();
      this.mode = this.dataset.mode || 'pdp';
      this.membershipVariantId = this.dataset.membershipVariantId;
      this.membershipSellingPlanId = this.dataset.membershipSellingPlanId;
      this.discountPct = parseInt(this.dataset.discountPct || '25', 10);
      this.toggle = this.querySelector('[data-membership-toggle]');
      this.errorEl = this.querySelector('[data-membership-error]');
      this.toggleWrap = this.querySelector('[data-membership-toggle-wrap]');
      this.cartDrawer = document.querySelector('cart-drawer');
    }

    connectedCallback() {
      if (!this.toggle || !this.membershipVariantId) return; 

      if (this.mode === 'pdp') {
        this.initPdp();
      } else if (this.mode === 'cart') {
        this.initCart();
      }
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      if (this.form && this.boundOnSubmit) {
        this.form.removeEventListener('submit', this.boundOnSubmit, true);
      }
    }

    initPdp() {
      const formId = this.dataset.productFormId;
      if (!formId) return;
      this.form = document.getElementById(formId)?.querySelector('form');
      if (!this.form) return;

      this.boundOnSubmit = this.onPdpSubmit.bind(this);
      this.form.addEventListener('submit', this.boundOnSubmit, true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event?.data?.variant;
          if (variant && typeof variant.price === 'number') {
            this.updatePrices(variant.price);
          }
        });
      }
    }

    initCart() {
      // Switching the toggle on adds the membership to the cart there and then
      // (see onCartToggle), so the shopper sees the line item and the new total
      // before they reach checkout.
      //
      // This used to also intercept the drawer's Checkout link and add the
      // membership on the way out, to compensate for the toggle rendering
      // pre-ticked (a pre-ticked state fires no `change` event, so nothing was
      // ever added). That interception was unreliable — the drawer replaces its
      // own markup on every cart update, so the captured checkout link went
      // stale and the add silently never happened — and it is unnecessary now
      // the toggle starts off. Removed: it was also the only path that could add
      // a second membership to a cart that already had one.
      this.toggle.addEventListener('change', this.onCartToggle.bind(this));
    }

    updatePrices(basePrice) {
      const memberEl = this.querySelector('[data-member-price]');
      const nonMemberEl = this.querySelector('[data-non-member-price]');
      if (!memberEl || !nonMemberEl) return;
      const memberPrice = Math.round((basePrice * (100 - this.discountPct)) / 100);
      memberEl.dataset.basePrice = String(basePrice);
      memberEl.textContent = `Members – ${this.formatMoney(memberPrice)}`;
      nonMemberEl.textContent = `Non-Members – ${this.formatMoney(basePrice)}`;
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents);
      }
      return `€${(cents / 100).toFixed(2)}`;
    }

    async onPdpSubmit(event) {
      if (!this.toggle.checked) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearError();

      const submitButton = this.form.querySelector('[name="add"]');
      submitButton?.classList.add('disabled');
      submitButton?.setAttribute('disabled', 'disabled');
      this.toggleWrap?.classList.add('membership-widget__toggle--loading');

      try {
        const formData = new FormData(this.form);
        const mainItem = this.formDataToItem(formData);
        const items = [mainItem, this.buildMembershipItem()];

        const sections = this.cartDrawer
          ? this.cartDrawer.getSectionsToRender().map((s) => s.section).join(',')
          : '';

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname,
          }),
        });

        const json = await response.json();

        if (json.status) {
          this.showError(json.description || json.message || 'Could not add to cart.');
          return;
        }

        if (this.cartDrawer && typeof this.cartDrawer.renderContents === 'function') {
          this.cartDrawer.renderContents(json);
        }
      } catch (err) {
        console.error(err);
        this.showError('Something went wrong. Please try again.');
      } finally {
        submitButton?.classList.remove('disabled');
        submitButton?.removeAttribute('disabled');
        this.toggleWrap?.classList.remove('membership-widget__toggle--loading');
      }
    }

    async onCartToggle(event) {
      if (!event.target.checked) return;

      this.clearError();
      this.toggleWrap?.classList.add('membership-widget__toggle--loading');
      this.toggle.disabled = true;

      try {
        const sections = this.cartDrawer
          ? this.cartDrawer.getSectionsToRender().map((s) => s.section).join(',')
          : '';

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [this.buildMembershipItem()],
            sections,
            sections_url: window.location.pathname,
          }),
        });

        const json = await response.json();

        if (json.status) {
          this.showError(json.description || json.message || 'Could not add membership.');
          this.toggle.checked = false;
          return;
        }

        if (this.cartDrawer && typeof this.cartDrawer.renderContents === 'function') {
          this.cartDrawer.renderContents(json, false);
        }
      } catch (err) {
        console.error(err);
        this.showError('Something went wrong. Please try again.');
        this.toggle.checked = false;
      } finally {
        this.toggleWrap?.classList.remove('membership-widget__toggle--loading');
        this.toggle.disabled = false;
      }
    }

    buildMembershipItem() {
      const item = { id: parseInt(this.membershipVariantId, 10), quantity: 1 };
      if (this.membershipSellingPlanId) {
        item.selling_plan = parseInt(this.membershipSellingPlanId, 10);
      }
      return item;
    }

    formDataToItem(formData) {
      const item = {
        id: parseInt(formData.get('id'), 10),
        quantity: parseInt(formData.get('quantity') || '1', 10),
      };
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);

      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') {
          properties[match[1]] = value;
        }
      }
      if (Object.keys(properties).length > 0) item.properties = properties;

      return item;
    }

    showError(message) {
      if (this.errorEl) this.errorEl.textContent = message;
    }

    clearError() {
      if (this.errorEl) this.errorEl.textContent = '';
    }
  }

  customElements.define('membership-widget', MembershipWidget);
})();
